/**
 * Pestaña POSICIONES: la tabla, calculada.
 *
 * Tres cosas que esta pantalla hace y la del incumbente no:
 *  1. dice **con qué sistema de puntos** se calculó (abajo de cada tabla), así
 *     que la discusión con el reglamento en la mano se puede tener;
 *  2. dice **qué criterio desempató** cada posición, en vez de dejar al club
 *     adivinando por qué quedó quinto;
 *  3. marca los empates que ningún criterio resolvió como lo que son —empates—
 *     en lugar de fingir un orden.
 *
 * Y la cuarta, que es la estructural: los números salen de los partidos
 * cargados. Nadie sube una tabla.
 */
import type { StandingsRow } from '@scout/core';
import type { TFunction } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { Card, Empty } from '../../components/States';
import type { StandingsBlock } from '../../lib/api-client';
import { ratioLabel, tiebreakLabelKey } from '../../lib/competition';

function Row({ row, t }: { row: StandingsRow; t: TFunction }): JSX.Element {
  return (
    <tr>
      <td className="num">
        {row.position}
        {row.tied ? (
          <span className="hint" title={t('competition.tied')}>
            =
          </span>
        ) : null}
      </td>
      <td>
        <strong>{row.name}</strong>
        {row.tiebreak !== null ? (
          <div className="hint">
            {t('competition.brokenBy', { criterion: t(tiebreakLabelKey(row.tiebreak)) })}
          </div>
        ) : null}
      </td>
      <td className="num">{row.played}</td>
      <td className="num">{row.won}</td>
      <td className="num">{row.lost}</td>
      <td className="num">{row.setsWon}</td>
      <td className="num">{row.setsLost}</td>
      <td className="num">{ratioLabel(row.setRatio)}</td>
      <td className="num">{row.pointsScored}</td>
      <td className="num">{row.pointsAgainst}</td>
      <td className="num">{ratioLabel(row.pointRatio)}</td>
      <td className="num">
        {row.adjustment !== 0 ? (
          <span title={t('competition.adjustmentTitle', { points: row.adjustment, reason: '' })}>
            {row.adjustment}
          </span>
        ) : (
          <span className="hint">·</span>
        )}
      </td>
      <td className="num">
        <strong>{row.points}</strong>
      </td>
    </tr>
  );
}

function Block({ block }: { block: StandingsBlock }): JSX.Element {
  const t = useT();
  const title =
    block.groupName === undefined ? block.phaseName : `${block.phaseName} · ${block.groupName}`;

  return (
    <Card
      title={title}
      actions={
        <span className="hint">
          {t('competition.matchesCounted', {
            counted: block.matchesCounted,
            total: block.matchesCounted + block.matchesPending,
          })}
        </span>
      }
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="num">{t('competition.colPos')}</th>
              <th>{t('competition.colTeam')}</th>
              <th className="num">{t('competition.colPlayed')}</th>
              <th className="num">{t('competition.colWon')}</th>
              <th className="num">{t('competition.colLost')}</th>
              <th className="num">{t('competition.colSetsWon')}</th>
              <th className="num">{t('competition.colSetsLost')}</th>
              <th className="num">{t('competition.colSetRatio')}</th>
              <th className="num">{t('competition.colPointsFor')}</th>
              <th className="num">{t('competition.colPointsAgainst')}</th>
              <th className="num">{t('competition.colPointRatio')}</th>
              <th className="num">{t('competition.colAdjustment')}</th>
              <th className="num">{t('competition.colTablePoints')}</th>
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <Row key={row.teamId} row={row} t={t} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Con qué reglamento se calculó: la tabla tiene que ser discutible. */}
      <p className="hint">
        {t('competition.computedWith', {
          scoring: block.scoring.label ?? scoringSummary(block, t),
        })}
        {' · '}
        {t('competition.tiebreakTitle')}:{' '}
        {block.tiebreakers.map((criterion) => t(tiebreakLabelKey(criterion))).join(' → ')}
      </p>
    </Card>
  );
}

/** Resumen del sistema cuando la competencia no le puso etiqueta. */
function scoringSummary(block: StandingsBlock, t: TFunction): string {
  const rules = block.scoring.rules.map((rule) =>
    t('competition.scoringRule', {
      winner: rule.winnerSets,
      loser: rule.loserSets,
      winnerPoints: rule.winnerPoints,
      loserPoints: rule.loserPoints,
    }),
  );
  if (rules.length > 0) return rules.join(' · ');
  return t('competition.scoringFallback', {
    winnerPoints: block.scoring.winPoints,
    loserPoints: block.scoring.lossPoints,
  });
}

export function StandingsTab({ blocks }: { blocks: readonly StandingsBlock[] }): JSX.Element {
  const t = useT();
  if (blocks.length === 0) {
    return (
      <Card>
        <Empty>{t('competition.standingsEmpty')}</Empty>
      </Card>
    );
  }
  return (
    <>
      <p className="hint">{t('competition.standingsIntro')}</p>
      {blocks.map((block) => (
        <Block key={`${block.phaseId}:${block.groupId ?? ''}`} block={block} />
      ))}
    </>
  );
}
